import { Box, Tooltip, Typography } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { AlertType, type ClaimItemRowForecast, type ValidationError } from "@/types";
import { outsidePolicyLineText, type OutsidePolicyLine } from "../../utils/outsidePolicy";

/**
 * "On approve" for one claim line (artboard 2a, Claim lines tab): what the
 * loader will do with this row. Errors first — a line that fails validation
 * never loads, whatever the forecast says.
 *
 *   - "Can't load · <reason>"  red pill, error-severity validation issue
 *   - "On file"                quiet; a matching line is already loaded
 *   - "Loads"                  quiet; the loader inserts it (a warning glyph
 *                              when a transformation warning rode along)
 *   - "—"                      no forecast (projection skipped / loading)
 *
 *   - "Loads · doesn't count"   the line is outside the policy's paid or
 *                              incurred window (#7436): it loads but never
 *                              counts toward the deductible. Warning tone with
 *                              the failed date beneath ("Paid Dec 20, 2025 ·
 *                              before paid period") until the reviewer
 *                              confirms the dates on the rail, then quiet.
 */
export const ClaimLineOutcomeCell = ({
  forecast,
  errors,
  excluded = false,
  outside = null,
  outsideAcknowledged = false,
}: {
  forecast: ClaimItemRowForecast | undefined;
  errors: ValidationError[];
  excluded?: boolean;
  /** The failed window check for this line, when the projection flagged it (#7436). */
  outside?: OutsidePolicyLine | null;
  /** The rail's "Dates are right" has been clicked: drop the warning tone. */
  outsideAcknowledged?: boolean;
}) => {
  const blocking = errors.filter((e) => e.severity === AlertType.ERROR);
  const warnings = errors.filter((e) => e.severity === AlertType.WARNING);

  if (excluded) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
        Excluded
      </Typography>
    );
  }
  if (blocking.length > 0) {
    const reason = blocking[0].message;
    return (
      <Tooltip title={blocking.map((e) => e.message).join("\n")} placement="top" arrow>
        <Box
          component="span"
          data-testid="claim-line-cant-load"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            height: 24,
            px: 1,
            maxWidth: "100%",
            borderRadius: "12px",
            bgcolor: "error.light",
            color: "error.dark",
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 14, flexShrink: 0 }} />
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            Can&apos;t load · {reason}
          </Box>
        </Box>
      </Tooltip>
    );
  }
  if (!forecast) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  if (forecast.operation === "update" || forecast.is_duplicate_on_file) {
    return (
      <Tooltip
        title="A matching claim line is already on file. Approving refreshes it in place rather than adding a duplicate."
        placement="top"
        arrow
      >
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
          On file
        </Typography>
      </Tooltip>
    );
  }
  if (outside) {
    const tone = outsideAcknowledged ? "text.secondary" : "warning.dark";
    return (
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }} data-testid="claim-line-doesnt-count">
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
          {!outsideAcknowledged && <WarningAmberRoundedIcon sx={{ fontSize: 16, color: "warning.main" }} aria-hidden />}
          <Typography
            variant="body2"
            sx={{ whiteSpace: "nowrap", color: tone, fontWeight: outsideAcknowledged ? 400 : 600 }}
          >
            Loads · doesn&apos;t count
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: tone, whiteSpace: "nowrap", lineHeight: 1.3 }}>
          {outsidePolicyLineText(outside)}
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
        Loads
      </Typography>
      {warnings.length > 0 && (
        <Tooltip title={warnings.map((e) => e.message).join("\n")} placement="top" arrow>
          <WarningAmberRoundedIcon sx={{ fontSize: 16, color: "warning.main" }} data-testid="claim-line-warning" />
        </Tooltip>
      )}
    </Box>
  );
};
